// VFR Devis Digital - API Client
// Base API client for backend communication

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  skipAuth?: boolean
}

// Session ID getter - will be set by auth store
let getSessionId: (() => string | null) | null = null

export function setSessionIdGetter(getter: () => string | null): void {
  getSessionId = getter
}

// Unauthorized handler — invoked when backend returns 401
// Wired in main.ts to logout + redirect to /login
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, skipAuth = false } = options

    // Add session header if available and not skipped
    const sessionId = !skipAuth && getSessionId ? getSessionId() : null
    console.log('[ApiClient] Request:', method, endpoint, 'sessionId:', sessionId ? sessionId.slice(0, 8) + '...' : 'none')
    const authHeaders: Record<string, string> = sessionId
      ? { 'x-session-id': sessionId }
      : {}

    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...headers
      }
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config)

    if (response.status === 401 && !skipAuth && onUnauthorized) {
      onUnauthorized()
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP Error: ${response.status}`)
    }

    return response.json()
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body })
  }

  async put<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const sessionId = getSessionId ? getSessionId() : null
    const headers: Record<string, string> = {}
    if (sessionId) {
      headers['x-session-id'] = sessionId
    }
    // No Content-Type — browser sets multipart/form-data with boundary

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData
    })

    if (response.status === 401 && onUnauthorized) {
      onUnauthorized()
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP Error: ${response.status}`)
    }

    return response.json()
  }
}

export const apiClient = new ApiClient(API_BASE_URL)
export default apiClient
